import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { IELAsyncAutocompleteElement } from '@el-async-autocomplete';
import {
  DataListTableActionElement,
  DataListTableDataElement,
  DataListTableElement,
  DataListTableLinkDataElement,
  DLT_DATA_TYPES,
} from '@el-data-list-table';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { Subject, Subscription, takeUntil } from 'rxjs';

import {
  ASSET_CATEGORY_REFERENCE_CATEGORY_FIELD,
  DB_COLLECTION_NAMES,
  INVENTORY_REFERENCE_CATEGORIES,
  INVENTORY_REFERENCE_SECTION,
  INVENTORY_STATUS,
  MODULES,
  PERMISSION_ACTIONS,
} from '@shared/constants';
import {
  IAssign,
  IIdentity,
  IIdentityResponse,
  IInventory,
  IProfilePicture,
  IReferenceCategoryResponse,
} from '@shared/interfaces';
import { getModulePermission } from '@shared/utils';

import { DIALOG_SIZES } from '../../../../constants';
import { CISService } from '../../../../services/cis.service';
import { DateTimeFormatService } from '../../../../services/date-time-format.service';
import { FilesService } from '../../../../services/files.service';
import { FlowControlService } from '../../../../services/flow-control.service';
import {
  ICreateAction,
  ICreateData,
  ISpeedDialButtons,
  PermissionFiltrationService,
} from '../../../../services/permission-filtration.service';
import {
  SnackbarService,
  SUCCESS_TYPES,
} from '../../../../services/snackbar.service';
import { IdentitiesInternalService } from '../../../identities/services/identities.internal.service';
import { IdentitiesService } from '../../../identities/services/identities.service';
import { ModulesService } from '../../../modules/services/modules.service';
import {
  ReferenceCategoryService,
  ReferenceService,
} from '../../../references/services';
import { IActionButton } from '../../../shared/components/action-button/action-button.component';
import {
  CONFIRMATION_TYPES,
  GetUserConfirmationService,
} from '../../../shared/services/get-user-confirmation/service';
import { getAllCISChipsList } from '../../data/cis.filters';
import { AssetAllocateComponent } from '../../popups/asset-allocate/asset-allocate.component';
import { AssetAllocateErrorPopupComponent } from '../../popups/asset-allocate-error-popup/asset-allocate-error-popup.component';
import { AssetAllocateHistoryPopupComponent } from '../../popups/asset-allocate-history/asset-allocate-history';
import { historyData } from '../../popups/asset-allocate-history/asset-allocate-history';
import { ImagePopupDialogComponent } from '../../popups/image-popup/image-popup.component';
import { ReturnAssetComponent } from '../../popups/return-popup/return-popup.component';
import { InventoryService } from '../../services/inventory.service';

const SPEED_DIAL = {
  ADD_NEW: 'ADD_NEW',
};

const ACTIONS = {
  VIEW_IMAGE: 'VIEW_IMAGE',
  ASSET_ALLOCATE: 'ASSET_ALLOCATE',
  ASSET_RETURN: 'ASSET_RETURN',
  VIEW_HISTORY: 'VIEW_HISTORY',
  EDIT: 'EDIT',
  DELETE: 'DELETE',
  RESTORE: 'RESTORE',
};
export interface ASSET {
  lend_to: string;
  lend_on_date: Date;
  return_on_date?: Date;
  return_state: boolean;
  remarks?: string;
}

//TODO:RETURN_ASSET/RETURN_ASSET needed to Update

@Component({
  selector: 'app-inventory',
  templateUrl: './inventory.component.html',
  styleUrls: ['./inventory.component.scss'],
})
export class InventoryComponent implements OnInit, OnDestroy {
  @Input() inventoryId: string;
  @Output() isLoading: EventEmitter<boolean> = new EventEmitter();
  @Output() updatedProfileImages: EventEmitter<IProfilePicture[]> =
    new EventEmitter();
  @Input() profilePictures: IProfilePicture[];
  @Input() inventory: IInventory;
  @Input() assignAsset: IAssign;
  @Input() allIdentities: IIdentity[];

  @ViewChild('modalContent', { static: true }) modalContent: TemplateRef<any>;

  constructor(
    private router: Router,
    route: ActivatedRoute,
    private flowControlService: FlowControlService,
    private translate: TranslateService,
    private permissionFiltrationService: PermissionFiltrationService,
    private cisService: CISService,
    private inventoryService: InventoryService,
    private identityInternalService: IdentitiesInternalService,
    private moduleService: ModulesService,
    private dateTimeFormatService: DateTimeFormatService,
    private referenceService: ReferenceService,
    private referenceCategoryService: ReferenceCategoryService,
    private dialog: MatDialog,
    private filesService: FilesService,
    private snackbar: SnackbarService,
    private identitiesService: IdentitiesService,
    private getUserConfirmation: GetUserConfirmationService
  ) {
    this.flowControlService.updateRouteInfo(
      this.router.url.split('?')[0],
      route.snapshot.data
    );
  }

  allPossibleKeys = ['is_search', 'status', 'assetId', 'category'];
  allActionButtons: ISpeedDialButtons[] = [];
  filteredActionButtons: IActionButton[] = [];
  assignee: IELAsyncAutocompleteElement[] = [];
  filteredInvitees: any;
  selectedUsers: IELAsyncAutocompleteElement;
  categoryList: { key: string; value: string }[] = [];
  inventoryListArray: DataListTableElement[] = [];
  historyData: historyData[];
  // query params
  start = 0;
  size = 10;
  totalCount = 0;

  isListLoading = true;
  private onDestroy$ = new Subject<void>();
  minDate = new Date();
  refresh = new Subject<void>();
  lend_to: string;
  identities: IIdentity[] = [];
  isIdentitiesModuleEnabled: boolean;
  identitiesModulesSub: Subscription;
  usersList: DataListTableElement[] = [];
  events: any[];
  resources: string[];
  referenceCategories: IReferenceCategoryResponse[];

  ngOnInit() {
    this.filterSpeedDialButtons();
    this.checkModulesEnabled();
    this.listenToReferences();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
    this.identitiesModulesSub.unsubscribe();
  }

  private handleSearchButtonClickEvent() {
    this.start = 0;
    this.size = 10;
    this.searchInventory();
  }

  filterSpeedDialButtons() {
    this.allActionButtons = [
      {
        data: {
          action: SPEED_DIAL.ADD_NEW,
          icon: 'add',
          tooltip: this.translate.instant('inventory.create.add-new-button'),
          id: 'btn-add-new-item',
        },
        allowedPermissions: getModulePermission(MODULES.INVENTORY, [
          PERMISSION_ACTIONS.WRITE,
        ]),
      },
    ];
    this.filteredActionButtons =
      this.permissionFiltrationService.filterSpeedDialButtons(
        this.allActionButtons
      );
  }

  onClickAction(action: string) {
    this.isListLoading = true;
    if (action === SPEED_DIAL.ADD_NEW) {
      this.router.navigate(['/app/inventory/create']);
    }
  }

  private checkModulesEnabled() {
    this.identitiesModulesSub = this.moduleService
      .listenToAvailability([MODULES.IDENTITIES])
      .subscribe((change) => {
        this.isIdentitiesModuleEnabled = change;
      });
  }

  showMore(shouldShowMore: boolean): void {
    if (shouldShowMore) {
      this.start = this.inventoryListArray.length;
      this.searchInventory(true);
    }
  }

  pageSizeChange(e: number) {
    if (e) {
      this.size = e;
    }
  }

  action(e: { action: string; data: IInventory; index: number }) {
    switch (e.action) {
      case ACTIONS.VIEW_HISTORY:
        this.onClickViewHistory(e.data as IInventory, e.index);
        break;
      case ACTIONS.ASSET_RETURN:
        this.onClickReturnAsset(e.data as IInventory, e.index);
        break;
      case ACTIONS.ASSET_ALLOCATE:
        this.onClickAssetAllocate(e.data as IInventory, e.index);
        break;
      case ACTIONS.EDIT:
        this.onClickEdit(e.data as IInventory, e.index);
        break;
      case ACTIONS.VIEW_IMAGE:
        this.onClickViewImage(e.data as IInventory, e.index);
        break;

      case ACTIONS.DELETE:
        this.onClickDelete(e.data as IInventory, e.index);
        break;

      default:
        break;
    }
  }

  onDataElementClick(e) {
    switch (e.action) {
      case ACTIONS.VIEW_IMAGE:
        this.onClickViewImage(
          this.inventoryListArray[e.index].originalData,
          e.index
        );
        break;
    }
  }

  private change_loading_state(index: number, is_loading: boolean) {
    this.inventoryListArray[index.valueOf()].loading = is_loading;
    this.inventoryListArray[index.valueOf()].expanded = !is_loading;
  }

  searchInventory(replaceUrl = false) {
    this.isListLoading = true;

    const { httpParams, onFinishSearch } = this.cisService.getSearchParams(
      this.start,
      this.size
    );

    let usedCISChipsList = httpParams.keys();
    usedCISChipsList = usedCISChipsList.filter(
      (chip) => chip !== 'is_search' && chip !== 'start' && chip !== 'size'
    );

    this.cisService.setFrequentlyUsedChipsFor(
      DB_COLLECTION_NAMES.INVENTORY,
      usedCISChipsList
    );

    this.inventoryService
      .getAllInventory(httpParams)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (res) => {
          this.totalCount = res?.count;
          this.inventoryListArray =
            this.start > 0 ? this.inventoryListArray : [];

          res.data.forEach((workflow) => {
            const index = this.inventoryListArray.length;
            const actionElements = this.get_action_elements(workflow);
            const dataElements = this.get_data_elements(workflow);

            this.inventoryListArray.push({
              loading: false,
              expanded: false,
              data: dataElements,
              icon: 'laptop',
              actions: actionElements,
              index,
              originalData: workflow,
            });
          });

          this.isListLoading = false;
          onFinishSearch({ replaceUrl });
        },
        error: () => {
          this.isListLoading = false;
        },
      });
  }
  getAgentNameString(name: IIdentity) {
    return [name?.salutation, name?.first_name, name?.last_name].join(' ');
  }

  private get_data_elements(
    inventory: IInventory,
    identities?: IIdentity[]
  ): DataListTableDataElement[] {
    this.minDate = inventory.purchase_date;
    const dataElements: ICreateData[] = [];
    let status;
    let color;
    if (inventory.status === INVENTORY_STATUS.DECOMMISSIONED) {
      status = this.translate.instant('inventory.dlt.decommissioned');
      color = 'red';
    } else if (inventory.status === INVENTORY_STATUS.AVAILABLE) {
      status = this.translate.instant('inventory.dlt.available');
      color = 'blue';
    } else if (inventory.status === INVENTORY_STATUS.ALLOCATE) {
      status = this.translate.instant('inventory.dlt.allocated');
      color = 'green';
    } else {
      inventory = this.translate.instant('inventory.dlt.Error');
      color = 'yellow';
    }

    const createdByUser = identities?.find(
      (item) => item._id?.toString() === inventory.created_by.toString()
    );
    const createdByUserName = createdByUser
      ? `${createdByUser.salutation} ${createdByUser.first_name} ${
          createdByUser?.last_name || ''
        }`
      : `-`;

    const updatedByUser = identities?.find(
      (item) => item._id?.toString() === inventory.changed_by.toString()
    );

    const updatedByUserName = updatedByUser
      ? `${updatedByUser.salutation} ${updatedByUser.first_name} ${
          updatedByUser?.last_name || ''
        }`
      : `-`;

    dataElements.push(
      {
        data: {
          key: this.translate.instant('inventory.dlt.asset-id'),
          value: inventory.assetId,
          type: DLT_DATA_TYPES.PLAIN,
        },

        allowedPermissions: getModulePermission(MODULES.INVENTORY, [
          PERMISSION_ACTIONS.READ,
        ]),
      },
      {
        data: {
          key: this.translate.instant('inventory.dlt.category'),
          value: inventory?.category.toString(),
          type: DLT_DATA_TYPES.PLAIN,
        },

        allowedPermissions: getModulePermission(MODULES.INVENTORY, [
          PERMISSION_ACTIONS.READ,
        ]),
      },
      {
        data: {
          key: this.translate.instant('inventory.dlt.view-image'),
          type: DLT_DATA_TYPES.BUTTON,
          action: ACTIONS.VIEW_IMAGE,
          face: this.translate.instant('inventory.dlt.tooltip.view-image'),
          backgroundColor: '#e3e3e3',
          icon: 'visibility',
          textColor: 'black',
        },
        allowedPermissions: getModulePermission(MODULES.SUBMISSIONS, [
          PERMISSION_ACTIONS.READ,
        ]),
      },
      {
        data: {
          key: this.translate.instant('inventory.dlt.serial-number'),
          value: inventory.serialNumber,
        },
        allowedPermissions: getModulePermission(MODULES.INVENTORY, [
          PERMISSION_ACTIONS.READ,
        ]),
      },
      {
        data: {
          key: this.translate.instant('inventory.dlt.manufacturer'),
          value: inventory.manufacture,
        },
        allowedPermissions: getModulePermission(MODULES.INVENTORY, [
          PERMISSION_ACTIONS.READ,
        ]),
      },

      {
        data: {
          key: this.translate.instant('inventory.dlt.vendor'),
          value: inventory.vendor,
        },
        allowedPermissions: getModulePermission(MODULES.INVENTORY, [
          PERMISSION_ACTIONS.READ,
        ]),
      },

      {
        data: {
          key: this.translate.instant('inventory.dlt.model'),
          value: inventory.model,
        },
        allowedPermissions: getModulePermission(MODULES.INVENTORY, [
          PERMISSION_ACTIONS.READ,
        ]),
      },
      {
        data: {
          key: this.translate.instant('inventory.dlt.purchase-date'),
          value: moment(inventory.purchase_date).format(' DD/MM/YYYY'),
        },
        allowedPermissions: getModulePermission(MODULES.INVENTORY, [
          PERMISSION_ACTIONS.READ,
        ]),
      },
      {
        data: {
          key: this.translate.instant('inventory.dlt.warranty-end-in'),
          value: moment(inventory.warranty_end_date).format(' DD/MM/YYYY'),
        },
        allowedPermissions: getModulePermission(MODULES.INVENTORY, [
          PERMISSION_ACTIONS.READ,
        ]),
      },
      {
        data: {
          key: this.translate.instant('inventory.dlt.status'),
          value: status,
          type: DLT_DATA_TYPES.CHIP,
          chipBackgroundColor: color,
          chipColor: 'white',
        },
        allowedPermissions: getModulePermission(MODULES.INVENTORY, [
          PERMISSION_ACTIONS.READ,
        ]),
      },
      {
        data: {
          type: DLT_DATA_TYPES.CHIP,
          value: '',
          key: '',
        },
      },
      {
        data: {
          key: this.translate.instant('inventory.dlt.description'),
          value: inventory.description ?? '-',
          type: DLT_DATA_TYPES.PLAIN,
        },
        allowedPermissions: getModulePermission(MODULES.INVENTORY, [
          PERMISSION_ACTIONS.READ,
        ]),
      },

      {
        data: {
          type: DLT_DATA_TYPES.SEPARATOR,
        },
      },
      {
        data: {
          key: this.translate.instant('inventory.dlt.created-by'),
          value: createdByUserName,
          type: this.isIdentitiesModuleEnabled
            ? DLT_DATA_TYPES.LINK
            : DLT_DATA_TYPES.PLAIN,
          href: '/app/identities',
          hrefParams: {
            is_search: true,
            _id: inventory.created_by?.toString(),
            username: createdByUser?.username,
          },
          linkType: 'internal',
          lazy: !createdByUser,
          loading: !createdByUser,
        } as DataListTableLinkDataElement,
        allowedPermissions: getModulePermission(MODULES.INVENTORY, [
          PERMISSION_ACTIONS.READ,
        ]),
      },
      {
        data: {
          key: this.translate.instant('inventory.dlt.created-date-time'),
          value: this.dateTimeFormatService.formatDateTime(
            inventory.created_on
          ),
        },
        allowedPermissions: getModulePermission(MODULES.INVENTORY, [
          PERMISSION_ACTIONS.READ,
        ]),
      },
      // changed by
      {
        data: {
          key: this.translate.instant('inventory.dlt.last-updated-by'),
          value: updatedByUserName,
          type: this.isIdentitiesModuleEnabled
            ? DLT_DATA_TYPES.LINK
            : DLT_DATA_TYPES.PLAIN,
          href: '/app/identities',
          hrefParams: {
            is_search: true,
            _id: inventory.changed_by?.toString(),
            username: updatedByUser?.username,
          },
          linkType: 'internal',
          lazy: !updatedByUserName,
          loading: !updatedByUserName,
        } as DataListTableLinkDataElement,
        allowedPermissions: getModulePermission(MODULES.INVENTORY, [
          PERMISSION_ACTIONS.READ,
        ]),
      },
      {
        data: {
          key: this.translate.instant('inventory.dlt.last-updated-date-time'),
          value: this.dateTimeFormatService.formatDateTime(
            inventory.last_modified_on
          ),
        },
        allowedPermissions: getModulePermission(MODULES.INVENTORY, [
          PERMISSION_ACTIONS.READ,
        ]),
      }
    );
    // Additional rows for ALLOCATE status
    if (inventory.status === INVENTORY_STATUS.ALLOCATE) {
      const lastAssignedAsset =
        inventory.assign_asset?.[inventory.assign_asset.length - 1];
      const today = new Date();
      const due_date = lastAssignedAsset?.return_on_date
        ? new Date(lastAssignedAsset.return_on_date)
        : null;
      let due_out = '  ';
      color = 'white';
      if (due_date != null && due_date > today) {
        const differenceInTime = Math.ceil(
          (due_date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        );
        due_out = moment(due_date).format('DD/MM/YYYY');
        due_out += ' ( ' + differenceInTime + ' )';
        color = 'gray';
      } else if (due_date != null) {
        due_out = moment(due_date).format('DD/MM/YYYY');
        due_out += ' - ';
        color = 'red';
      } else {
        due_out = ' - ';
        color = 'red';
      }

      dataElements.push(
        {
          data: {
            type: DLT_DATA_TYPES.SEPARATOR,
          },
        },
        {
          data: {
            key: this.translate.instant('inventory.dlt.lend-to'),

            value: this.getAgentNameString(
              identities?.find(
                (identity) => lastAssignedAsset.lend_to === identity._id
              )
            ),
            type: this.isIdentitiesModuleEnabled
              ? DLT_DATA_TYPES.LINK
              : DLT_DATA_TYPES.PLAIN,
            href: '/app/identities',
            hrefParams: {
              is_search: true,
              _id: lastAssignedAsset.lend_to?.toString(),
              username: 'fullname',
            },
            linkType: 'internal',
            lazy: !lastAssignedAsset.lend_to,
            loading: !lastAssignedAsset.lend_to,
          } as DataListTableLinkDataElement,
          allowedPermissions: getModulePermission(MODULES.IDENTITIES, [
            PERMISSION_ACTIONS.READ,
          ]),
        },

        {
          data: {
            key: this.translate.instant('inventory.dlt.lend-on-date'),
            value: moment(lastAssignedAsset.lend_on_date).format(' DD/MM/YYYY'),
            type: DLT_DATA_TYPES.PLAIN,
          },
          allowedPermissions: getModulePermission(MODULES.INVENTORY, [
            PERMISSION_ACTIONS.READ,
          ]),
        },
        {
          data: {
            key: this.translate.instant('inventory.dlt.return-on-date'),
            value: due_out,
            type: DLT_DATA_TYPES.CHIP,
            chipBackgroundColor: color,
            chipColor: 'white',
          },
          allowedPermissions: getModulePermission(MODULES.INVENTORY, [
            PERMISSION_ACTIONS.READ,
          ]),
        }
      );
    } else if (inventory.status === INVENTORY_STATUS.DECOMMISSIONED) {
      dataElements.push(
        {
          data: {
            type: DLT_DATA_TYPES.SEPARATOR,
          },
        },
        {
          data: {
            key: this.translate.instant('inventory.dlt.decommissioned-by'),
            value: updatedByUserName,
            type: this.isIdentitiesModuleEnabled
              ? DLT_DATA_TYPES.LINK
              : DLT_DATA_TYPES.PLAIN,
            href: '/app/identities',
            hrefParams: {
              is_search: true,
              _id: inventory.changed_by?.toString(),
              username: updatedByUser?.username,
            },
            linkType: 'internal',
            lazy: !updatedByUserName,
            loading: !updatedByUserName,
          } as DataListTableLinkDataElement,
          allowedPermissions: getModulePermission(MODULES.INVENTORY, [
            PERMISSION_ACTIONS.READ,
          ]),
        },
        {
          data: {
            key: this.translate.instant('inventory.dlt.decommissioned-at'),
            value: this.dateTimeFormatService.formatDateTime(
              inventory.last_modified_on
            ),
          },
          allowedPermissions: getModulePermission(MODULES.INVENTORY, [
            PERMISSION_ACTIONS.READ,
          ]),
        }
      );
    }

    return this.permissionFiltrationService.getData(dataElements);
  }
  async populateChangedBy(index: number, inventory: IInventory): Promise<void> {
    const ids: string[] = [];
    const lastAssignedAsset =
      inventory.assign_asset?.[inventory.assign_asset.length - 1];
    const pid = lastAssignedAsset?.lend_to.toString();
    ids.push(inventory.created_by.toString());
    ids.push(pid);

    const uniqueIds: string[] = [...new Set(ids)].filter(
      (id) => id !== undefined
    );
    this.identityInternalService
      .getSetOfPublicIdentitiesFromConnection(uniqueIds)
      .subscribe({
        next: (res) => {
          this.identities.push(res.data);
        },
        error: () => {
          this.identities.push(undefined);
        },
        complete: async () => {
          this.inventoryListArray[index].data = this.get_data_elements(
            inventory,
            this.identities
          );
        },
      });
  }

  private get_action_elements(
    inventory: IInventory
  ): DataListTableActionElement[] {
    const actionElements: ICreateAction[] = [];
    switch (inventory?.status) {
      case 'AVAILABLE':
        actionElements.push(this.view_allocate_history(inventory));
        actionElements.push(this.create_asset_allocate(inventory));
        actionElements.push(this.create_edit_action(inventory));
        actionElements.push(this.create_delete_action(inventory));
        break;

      case 'ALLOCATE':
        actionElements.push(this.view_allocate_history(inventory));
        actionElements.push(this.create_asset_return_action(inventory));
        break;

      case 'DECOMMISSIONED':
        actionElements.push(this.view_allocate_history(inventory));
        break;
    }

    return this.permissionFiltrationService.getActions(actionElements);
  }

  private view_allocate_history(inventory: IInventory): ICreateAction {
    const action: DataListTableActionElement = {
      icon: 'history',
      face:
        ' ' + this.translate.instant('inventory.dlt.faces.allocate-history'),
      tooltip: this.translate.instant('inventory.dlt.tooltip.allocate-history'),
      action: ACTIONS.VIEW_HISTORY,
      data: inventory,
    };
    return {
      action,
      allowedPermissions: getModulePermission(MODULES.INVENTORY, [
        PERMISSION_ACTIONS.READ,
      ]),
    };
  }

  private create_asset_return_action(inventory: IInventory): ICreateAction {
    const action: DataListTableActionElement = {
      icon: 'keyboard_return',
      face: ' ' + this.translate.instant('inventory.dlt.faces.return'),
      tooltip: this.translate.instant('inventory.dlt.tooltip.return'),
      action: ACTIONS.ASSET_RETURN,
      data: inventory,
    };
    return {
      action,
      allowedPermissions: getModulePermission(MODULES.INVENTORY, [
        PERMISSION_ACTIONS.READ,
      ]),
    };
  }

  private create_asset_allocate(inventory: IInventory): ICreateAction {
    const action: DataListTableActionElement = {
      icon: 'work',
      face: ' ',
      tooltip: this.translate.instant('inventory.dlt.tooltip.make-a-allocate'),
      action: ACTIONS.ASSET_ALLOCATE,
      data: inventory,
    };
    return {
      action,
      allowedPermissions: getModulePermission(MODULES.INVENTORY, [
        PERMISSION_ACTIONS.READ,
      ]),
    };
  }

  private create_edit_action(inventory: IInventory): ICreateAction {
    const action: DataListTableActionElement = {
      icon: 'edit',
      face: '',
      tooltip: this.translate.instant('inventory.dlt.tooltip.edit'),
      action: ACTIONS.EDIT,
      data: inventory,
    };
    return {
      action,
      allowedPermissions: getModulePermission(MODULES.INVENTORY, [
        PERMISSION_ACTIONS.EDIT,
      ]),
    };
  }

  private create_delete_action(inventory: IInventory): ICreateAction {
    const action: DataListTableActionElement = {
      icon: 'delete_outline',
      face: '',
      tooltip: this.translate.instant('inventory.dlt.tooltip.delete'),
      action: ACTIONS.DELETE,
      data: inventory,
    };
    return {
      action,
      allowedPermissions: getModulePermission(MODULES.INVENTORY, [
        PERMISSION_ACTIONS.DELETE,
      ]),
    };
  }

  onExpand(index: number) {
    const workflow = this.inventoryListArray[index].originalData;
    this.populateChangedBy(index, workflow);
  }

  onClickViewHistory(inventory: IInventory, index: number) {
    this.change_loading_state(index, true);
    this.isLoading.emit(true);

    this.inventoryService.getInventoryById(inventory._id.toString()).subscribe({
      next: async (res) => {
        if (res.success) {
          if (res?.data.assign_asset.length > 0) {
            const identityCache = new Map<
              string,
              { name: string; avatar: string }
            >();

            const historyData = await Promise.all(
              res.data?.assign_asset
                .filter((asset) => asset.return_state)
                .map(async (asset) => {
                  const lend_id = asset.lend_to?.toString();

                  if (!lend_id) {
                    return {
                      action: 'Returned',
                      lend_on_date: moment(asset.lend_on_date).format(
                        'YYYY:MM DD'
                      ),
                      return_on_date: moment(asset.return_on_date).format(
                        'YYYY:MM DD'
                      ),
                      remarks: asset.remarks.toString(),
                      name: '',
                      avatar: '',
                    };
                  }

                  // Check if lend_id is already cached
                  if (identityCache.has(lend_id)) {
                    const cachedData = identityCache.get(lend_id);
                    return {
                      action: 'Returned',
                      lend_on_date: moment(asset.lend_on_date).format(
                        'YYYY:MM DD'
                      ),
                      return_on_date: moment(asset.return_on_date).format(
                        'YYYY:MM DD'
                      ),
                      remarks: asset.remarks.toString(),
                      name: cachedData.name,
                      avatar: cachedData.avatar,
                    };
                  }

                  let name = '';
                  let avatar = '';

                  try {
                    // Fetch identity and avatar if not cached
                    const response = await this.identitiesService
                      .getIdentity(lend_id)
                      .toPromise();
                    const identity = response.data;

                    // Construct full name
                    name = `${identity.salutation || ''} ${
                      identity.first_name
                    } ${identity.last_name || ''}`.trim();

                    // Fetch avatar
                    avatar = await this.filesService.getProfilePictureOneTime(
                      identity
                    );

                    // Cache the result
                    identityCache.set(lend_id, { name, avatar });
                  } catch (err) {
                    console.error(
                      `Error fetching data for lend_id ${lend_id}:`,
                      err
                    );
                  }

                  return {
                    action: 'Returned',
                    lend_on_date: moment(asset.lend_on_date).format(
                      'YYYY:MM DD'
                    ),
                    return_on_date: moment(asset.return_on_date).format(
                      'YYYY:MM DD'
                    ),
                    remarks: asset.remarks.toString(),
                    name,
                    avatar,
                  };
                })
            );

            // Open the dialog with populated history data
            this.dialog.open(AssetAllocateHistoryPopupComponent, {
              ...DIALOG_SIZES.MEDIUM,
              data: historyData,
            });
            this.isLoading.emit(false);
          } else {
            this.snackbar.warn(
              this.translate.instant('inventory.snackbar.no-history')
            );
            this.isLoading.emit(false);
          }
        }
      },
      error: () => {
        this.snackbar.error(
          this.translate.instant('inventory.snackbar.no-history')
        );
        this.isLoading.emit(false);
      },
    });

    this.change_loading_state(index, false);
  }

  //Image
  async generateAvatars(
    index: number,
    identity: IIdentityResponse
  ): Promise<void> {
    const imageData = await this.filesService.getProfilePictureOneTime(
      identity
    );

    this.usersList[index].avatar = imageData;
  }

  onClickEdit(inventory: IInventory, index: number) {
    this.change_loading_state(index, true);
    this.isLoading.emit(true);
    this.router.navigate([`/app/inventory/${inventory._id}/edit`]);
  }

  onClickReturnAsset(inventory: IInventory, index: number) {
    this.change_loading_state(index, true);
    this.isLoading.emit(true);

    const dialogRef = this.dialog.open(ReturnAssetComponent, {
      width: DIALOG_SIZES.MEDIUM.maxWidth,
      data: inventory,
    });

    dialogRef.afterClosed().subscribe((result) => {
      const updatedAssignAsset = [...inventory.assign_asset];
      const lastAsset = updatedAssignAsset[updatedAssignAsset.length - 1];

      const updatedAsset: ASSET = {
        ...lastAsset,
        lend_to: String(lastAsset.lend_to),
        return_on_date: new Date(result.return_on_date),
        return_state: true,
        remarks: String(result.remarks),
      };

      updatedAssignAsset[updatedAssignAsset.length - 1] = updatedAsset;

      const postData = {
        ...inventory,
        assign_asset: updatedAssignAsset,
        status: INVENTORY_STATUS.AVAILABLE,
      };

      this.inventoryService
        .returnAsset(postData, inventory._id.toString())
        .subscribe({
          next: (res) => {
            if (res.success) {
              this.update_item(res.data, index);
              this.refresh.next();
            }
            this.change_loading_state(index, false);
          },
          error: () => {
            this.snackbar.error(
              this.translate.instant('inventory.snackbar.return-asset-error')
            );
            this.dialog.open(AssetAllocateErrorPopupComponent, {
              ...DIALOG_SIZES.MEDIUM,
            });
            this.change_loading_state(index, false);
          },
        });

      this.snackbar.success(SUCCESS_TYPES.ASSET_RETURN);
    });
    this.isLoading.emit(false);
    this.change_loading_state(index, false);
  }

  onClickAssetAllocate(inventory: IInventory, index: number) {
    this.change_loading_state(index, true);
    this.isLoading.emit(true);

    const dialogRef = this.dialog.open(AssetAllocateComponent, {
      width: DIALOG_SIZES.MEDIUM.maxWidth,
      data: inventory,
    });

    dialogRef.afterClosed().subscribe((result) => {
      const newAsset: ASSET = {
        lend_to: result.lend_to,
        lend_on_date: new Date(result.lend_on_date),
        return_on_date: result.return_on_date,
        return_state: false,
        remarks: '',
      };
      const postData = {
        ...inventory,
        assign_asset: [...inventory.assign_asset, newAsset],
        status: INVENTORY_STATUS.ALLOCATE,
      };
      this.inventoryService
        .assignAsset(postData, inventory._id.toString())
        .subscribe({
          next: (res) => {
            if (res.success) {
              this.update_item(res.data, index);
              this.refresh.next();
            }
            this.change_loading_state(index, false);
          },
          error: () => {
            this.snackbar.error(
              this.translate.instant('inventory.snackbar.allocate-asset-error')
            );
            this.dialog.open(AssetAllocateErrorPopupComponent, {
              ...DIALOG_SIZES.MEDIUM,
            });
            this.change_loading_state(index, false);
          },
        });
      this.snackbar.success(SUCCESS_TYPES.ALLOCATE);
    });
    this.isLoading.emit(false);
    this.change_loading_state(index, false);
  }

  onClickViewImage(inventory: IInventory, index: number) {
    this.change_loading_state(index, true);
    this.isLoading.emit(true);
    this.inventoryService.getInventoryById(inventory._id.toString()).subscribe({
      next: (res) => {
        if (res.success) {
          if (res.data.asset_image.length > 0) {
            this.dialog.open(ImagePopupDialogComponent, {
              data: { asset_image: res.data.asset_image },
              width: DIALOG_SIZES.MEDIUM.maxWidth,
            });
          } else {
            this.snackbar.warn(
              this.translate.instant('inventory.snackbar.no-image')
            );
          }
        } else {
          this.snackbar.warn(
            this.translate.instant('inventory.snackbar.no-image')
          );
        }
        this.change_loading_state(index, false);
        this.isLoading.emit(false);
      },
      error: () => {
        this.snackbar.error(
          this.translate.instant('inventory.snackbar.no-image')
        );
        this.change_loading_state(index, false);
        this.isLoading.emit(false);
      },
    });
  }

  onClickDelete(inventory: IInventory, index: number) {
    // TODO: To be removed
    this.change_loading_state(index, true);
    const dialogData = {
      description: this.translate.instant(
        'inventory.snackbar.confirmation-delete'
      ),
    };
    this.getUserConfirmation
      .confirm(CONFIRMATION_TYPES.DELETE, dialogData)
      .then((result: boolean) => {
        if (result) {
          this.inventoryService
            .deleteInventoryById(inventory._id.toString())
            .subscribe({
              next: (res) => {
                this.inventoryListArray[index].originalData.status =
                  INVENTORY_STATUS.DECOMMISSIONED;
                this.update_item(res.data, index);
                this.change_loading_state(index, false);
              },
              error: () => {
                this.change_loading_state(index, false);
              },
            });
          this.snackbar.success(SUCCESS_TYPES.DECOMMISSIONED);
        } else {
          this.change_loading_state(index, false);
        }
      })
      .catch(() => {
        this.change_loading_state(index, false);
      });
  }

  listenToReferences() {
    this.referenceCategoryService.allCategories.subscribe(async (data) => {
      if (data && data.length === 0) {
        await this.referenceCategoryService.getAllReferenceCategories();
      }
      if (
        this.referenceCategories?.length === 0 ||
        this.referenceCategories === undefined
      ) {
        this.referenceCategories = data;

        const categoriesId = this.referenceCategories.find((category) => {
          return (
            INVENTORY_REFERENCE_SECTION.ASSET.toUpperCase() ===
              category.section.toUpperCase() &&
            category?.name?.trim()?.toUpperCase() ===
              INVENTORY_REFERENCE_CATEGORIES.CATEGORY.toUpperCase()
          );
        })?._id;

        if (categoriesId) {
          const availableResourcesId = this.referenceCategories.find(
            (category) =>
              INVENTORY_REFERENCE_SECTION.ASSET.toUpperCase() ===
                category.section.toUpperCase() &&
              category?.name?.trim()?.toUpperCase() ===
                INVENTORY_REFERENCE_CATEGORIES.CATEGORY.toUpperCase()
          )?._id;

          if (availableResourcesId) {
            await this.referenceService
              .getReferences(availableResourcesId?.toString())
              .then((data) => {
                this.resources = [];
                data?.forEach((data) => {
                  data.reference?.forEach((ref) => {
                    if (
                      ref.field.name.toUpperCase() ===
                        ASSET_CATEGORY_REFERENCE_CATEGORY_FIELD.ASSET_CATEGORY.toUpperCase() &&
                      !data?.is_deleted
                    ) {
                      this.resources.push(data.reference[0].value.toString());
                    }
                  });
                });
                this.cisService.initializesCIS(
                  this.allPossibleKeys,
                  getAllCISChipsList(this.translate, this.resources),
                  this.onDestroy$.asObservable(),
                  () => this.handleSearchButtonClickEvent(),
                  DB_COLLECTION_NAMES.INVENTORY
                );
              });
          }
        }
      }
    });
  }

  private update_item(inventory: IInventory, index: number): void {
    const actionElements = this.get_action_elements(inventory);
    const dataElements = this.get_data_elements(inventory);
    this.populateChangedBy(index, inventory);

    this.inventoryListArray[index].actions = actionElements;
    this.inventoryListArray[index].data = dataElements;
    this.inventoryListArray[index].originalData = inventory;
  }
}
